import React, { useEffect, useState } from 'react';
import { Card, Button, Input } from '../components/UI';
import { apiRequest } from '../utils/api';
import { PenaltyRecord } from '../types';
import { RefreshCw, Edit2, Trash2, Save, X, Search, ShieldAlert } from 'lucide-react';
import { PageHero } from '../components/PageHero';

export const PenaltyArchive: React.FC = () => {
    const [penalties, setPenalties] = useState<PenaltyRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editingPenalty, setEditingPenalty] = useState<PenaltyRecord | null>(null);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<PenaltyRecord[]>('penalties.php');
            setPenalties(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleSave = async () => {
        if (!editingPenalty) return;
        try {
            await apiRequest('penalties.php', 'PUT', editingPenalty);
            setEditingPenalty(null);
            loadData();
        } catch (e: any) {
            alert("Aktualisierung fehlgeschlagen: " + e.message);
        }
    };

    const handleDelete = async (id: number) => {
        if(!confirm("Diesen Strafeneintrag wirklich unwiderruflich löschen?")) return;
        try {
            await apiRequest(`penalties.php?id=${id}`, 'DELETE');
            loadData();
        } catch (e: any) {
            alert("Löschen fehlgeschlagen: " + e.message);
        }
    };

    const filtered = penalties.filter(p => 
        p.username?.toLowerCase().includes(search.toLowerCase()) || 
        p.event_name?.toLowerCase().includes(search.toLowerCase()) ||
        p.track_location?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Entscheidungsdatenbank"
                title="Strafenarchiv"
                description="Alle veröffentlichten Entscheidungen der laufenden Saison transparent und durchsuchbar an einem Ort."
                icon={ShieldAlert}
                accent="#f97316"
                stats={[
                    { label: 'Einträge', value: penalties.length },
                    { label: 'Treffer', value: filtered.length },
                    { label: 'Verwarnungen', value: penalties.filter(p => p.warning).length },
                ]}
                actions={<Button variant="secondary" icon={RefreshCw} onClick={loadData}>Aktualisieren</Button>}
            />

            <Card className="overflow-hidden rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.23)]">
                <div className="mb-6 relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input 
                        className="w-full bg-gray-900 border border-gray-700 rounded py-2 pl-10 pr-3 text-sm focus:border-f1-red outline-none"
                        placeholder="Fahrer oder Strecke suchen …"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                {loading ? <div className="text-center p-10"><RefreshCw className="animate-spin mx-auto"/></div> : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 uppercase text-xs font-bold">
                                <tr>
                                    <th className="p-3">Datum</th>
                                    <th className="p-3">Rennen</th>
                                    <th className="p-3">Fahrer</th>
                                    <th className="p-3">Regel</th>
                                    <th className="p-3">Strafe</th>
                                    <th className="p-3">Begründung</th>
                                    <th className="p-3 text-right">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {filtered.length === 0 && <tr><td colSpan={7} className="text-center p-6 text-gray-500">Keine Einträge gefunden.</td></tr>}
                                {filtered.map(p => (
                                    <tr key={p.id} className="hover:bg-gray-800/50">
                                        <td className="p-3 text-gray-500 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                                        <td className="p-3 font-bold">{p.track_location || p.event_name}</td>
                                        <td className="p-3 text-white font-bold">{p.username}</td>
                                        <td className="p-3 text-gray-400 font-mono">{p.rule_id || '-'}</td>
                                        <td className="p-3">
                                            <div className="flex flex-col gap-1">
                                                {p.points !== 0 && <span className={p.points < 0 ? 'text-red-400' : 'text-gray-400'}>{p.points} Pkt</span>}
                                                {p.time_penalty > 0 && <span className="text-yellow-500">+{p.time_penalty} Sek.</span>}
                                                {p.grid_penalty > 0 && <span className="text-orange-500">+{p.grid_penalty} Grid</span>}
                                                {p.warning && <span className="text-blue-400 uppercase text-[10px] border border-blue-500 px-1 rounded w-fit">Verwarnung</span>}
                                            </div>
                                        </td>
                                        <td className="p-3 text-gray-300 max-w-xs truncate" title={p.description}>{p.description}</td>
                                        <td className="p-3 text-right">
                                            <button onClick={() => setEditingPenalty(p)} className="p-1 hover:text-f1-red text-gray-500"><Edit2 size={16}/></button>
                                            <button onClick={() => handleDelete(p.id)} className="p-1 hover:text-red-600 text-gray-500 ml-2"><Trash2 size={16}/></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Edit Modal */}
            {editingPenalty && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-lg bg-f1-dark border-gray-700">
                        <div className="flex justify-between items-center mb-4 border-b border-gray-800 pb-2">
                            <h3 className="text-lg font-bold uppercase flex items-center gap-2">
                                <ShieldAlert size={18} className="text-f1-red"/> Strafe bearbeiten
                            </h3>
                            <button onClick={() => setEditingPenalty(null)}><X className="text-gray-400 hover:text-white"/></button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <Input 
                                    label="Strafpunkte" 
                                    type="number" 
                                    value={editingPenalty.points} 
                                    onChange={e => setEditingPenalty({...editingPenalty, points: parseInt(e.target.value)})}
                                />
                                <Input 
                                    label="Zeitstrafe (Sek.)" 
                                    type="number" 
                                    value={editingPenalty.time_penalty} 
                                    onChange={e => setEditingPenalty({...editingPenalty, time_penalty: parseInt(e.target.value)})}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <Input 
                                    label="Startplatzstrafe" 
                                    type="number" 
                                    value={editingPenalty.grid_penalty} 
                                    onChange={e => setEditingPenalty({...editingPenalty, grid_penalty: parseInt(e.target.value)})}
                                />
                                <div className="flex items-center pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="w-4 h-4 rounded bg-gray-800 border-gray-600"
                                            checked={editingPenalty.warning}
                                            onChange={e => setEditingPenalty({...editingPenalty, warning: e.target.checked})}
                                        />
                                        <span className="text-sm font-bold text-gray-300">Offizielle Verwarnung</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Begründung</label>
                                <textarea 
                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white h-20 text-sm"
                                    value={editingPenalty.description}
                                    onChange={e => setEditingPenalty({...editingPenalty, description: e.target.value})}
                                ></textarea>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 border-t border-gray-800 pt-4">
                            <Button variant="ghost" onClick={() => setEditingPenalty(null)}>Abbrechen</Button>
                            <Button onClick={handleSave} icon={Save}>Änderungen speichern</Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
